import type { MutableRefObject, ReactNode } from "react";
import { GraphLayout, type PageFormat } from "./placement/layoutOptions";
import type { GraphNodeContext } from "./placement/placeGraph";
import type { HiddenChildren } from "./placement/branches";
import type { DCEvent } from "@/events/eventsDomain";
import type { IconName } from "@/components-v2/iconMapping";
import type { TMessage, TMessageParam } from "@/i18n/tt";

export { GraphLayout };
export type { GraphNodeContext, HiddenChildren, PageFormat };

export type GraphUtils = {
  zoomIn: () => void;
  zoomOut: () => void;
  fitView: () => void;
  fitToPath: () => void;
  getNodeContext: (id: string) => GraphNodeContext | undefined;
  toPng: () => Promise<Blob | null>;
};

export type GraphNodeStyle = {
  surface: string;
  border: string;
  label: string;
  accent: string;
  inverted?: boolean;
};

export type GraphTheme = {
  canvas: string;
  page: string;
  pageBorder: string;
  edge: string;
  edgeLabel: string;
  nodes: Record<string, GraphNodeStyle>;
  hover: GraphNodeStyle;
};

export type GraphNodeStyleName = "gray" | "green" | "orange" | "contrast";

const tinted = (surface: string): GraphNodeStyle => ({
  surface,
  border: "var(--color-gray)",
  label: "var(--color-gray-x-hard)",
  accent: "var(--color-primary)",
});

export const DEFAULT_NODE_STYLE: GraphNodeStyle = tinted("light-dark(#eaedf2, #1c202b)");

const NODE_STYLES: Record<GraphNodeStyleName, GraphNodeStyle> = {
  gray: DEFAULT_NODE_STYLE,
  green: tinted("light-dark(#e7f3ef, #1d2724)"),
  orange: tinted("light-dark(#fbe8d5, #231f1f)"),
  contrast: {
    surface: "light-dark(#071232, #eaedf2)",
    border: "transparent",
    label: "var(--color-primary-contrast)",
    accent: "var(--color-primary-contrast)",
    inverted: true,
  },
};

export const DEFAULT_GRAPH_THEME: GraphTheme = {
  canvas: "var(--color-bg-base)",
  page: "var(--color-bg-default)",
  pageBorder: "var(--color-gray)",
  edge: "var(--color-gray-hard)",
  edgeLabel: "var(--color-gray-x-hard)",
  nodes: NODE_STYLES,
  hover: {
    surface: "light-dark(#e4eafd, #252a36)",
    border: "var(--color-primary)",
    label: "var(--color-gray-x-hard)",
    accent: "var(--color-primary)",
  },
};

export const nodeStyleOf = (theme: GraphTheme, name = "gray"): GraphNodeStyle =>
  theme.nodes[name] ?? DEFAULT_NODE_STYLE;

export type GraphNodeBadge = IconName | "dCompany";

export type GraphNodeFieldValue =
  | string
  | TMessage
  | TMessageParam
  | [type: "tag", value: string | TMessage | TMessageParam];

export type GraphNodeField = {
  value: GraphNodeFieldValue;
  align?: "left" | "right";
};

export type GraphNode<N> = {
  getId: (node: N) => string;
  getLabel: (node: N) => string | TMessage | TMessageParam;
  getFields?: (node: N) => GraphNodeField[];
  getIcon?: (node: N) => IconName | undefined;
  getBadges?: (node: N) => GraphNodeBadge[];
  getStyle?: (node: N) => string;
  getSelected?: (node: N) => boolean;
  getDimmed?: (node: N) => boolean;
  getPathEndpoint?: (node: N) => boolean;
  getBranchLayout?: (node: N) => GraphLayout | undefined;
  getHiddenChildren?: (node: N) => HiddenChildren | undefined;
  getGrouped?: (node: N) => boolean;
};

export type GraphEdge<E> = {
  getId: (edge: E) => string;
  getSource: (edge: E) => string;
  getTarget: (edge: E) => string;
  getLabel?: (edge: E) => string | TMessage | TMessageParam;
};

export type GraphPanels = {
  top?: ReactNode;
  bottom?: ReactNode;
};

export type DataGraphProps<N, E> = {
  nodeData: N[];
  edgeData: E[];
  graphNode: GraphNode<N>;
  graphEdge: GraphEdge<E>;
  layout?: GraphLayout;
  pageFormat?: PageFormat;
  theme?: GraphTheme;
  panels?: GraphPanels;
  originId?: string;
  highlightPath?: boolean;
  locked?: boolean;
  isLoading?: boolean;
  onNodeClick?: ((id: string) => void) | ((id: string) => DCEvent[]);
  getTooltip?: (id: string) => string | TMessage | TMessageParam;
  onCanvasClick?: (() => void) | (() => DCEvent[]);
  onLayoutComplete?: (() => void) | (() => DCEvent[]);
  graphRef?: MutableRefObject<GraphUtils | null>;
};
